import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './Invites.css';

function Invites() {
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [invitees, setInvitees] = useState([]);
  const [veraBalance, setVeraBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [displayCount, setDisplayCount] = useState(3); // Show 3 invitees initially

  useEffect(() => {
    loadData();

    // Setup Telegram WebApp Back Button
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.BackButton.show();
      tg.BackButton.onClick(() => navigate('/profile'));

      return () => {
        tg.BackButton.offClick(() => navigate('/profile'));
        tg.BackButton.hide();
      };
    }
  }, [navigate]);

  const loadData = async () => {
    try {
      const [inviteRes, inviteesRes, balanceRes] = await Promise.all([
        api.get('/user/invite-info'),
        api.get('/user/invitees'),
        api.get('/vera/balance')
      ]);

      setInviteInfo(inviteRes.data);
      setInvitees(inviteesRes.data.invitees || []);
      setVeraBalance(balanceRes.data);
    } catch (error) {
      console.error('Failed to load invites data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    if (inviteInfo?.invite_link) {
      navigator.clipboard.writeText(inviteInfo.invite_link).then(() => {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showAlert('Ссылка-приглашение скопирована!');
        }
      });
    }
  };

  const shareInviteLink = () => {
    if (inviteInfo?.invite_link && window.Telegram?.WebApp) {
      const text = `Присоединяйтесь к сообществу CyberDelia 🔐\n\nСеть, доступная только по приглашениям. Помоги сделать её ещё безопаснее!\n\nПо этой ссылке — 3 дня бесплатного доступа к тестированию и участию.\n\n${inviteInfo.invite_link}`;
      const url = `https://t.me/share/url?url=${encodeURIComponent(inviteInfo.invite_link)}&text=${encodeURIComponent(text)}`;
      window.Telegram.WebApp.openTelegramLink(url);
    }
  };

  const handleBuyInvites = async () => {
    if (buying) return;

    const veraPoints = veraBalance?.vera_points || 0;
    if (veraPoints < 300) {
      window.Telegram?.WebApp?.showAlert('Недостаточно VERA очков. Нужно минимум 300 очков для покупки 5 приглашений.');
      return;
    }

    setBuying(true);
    try {
      await api.post('/vera/buy-invites', { count: 5 });

      window.Telegram?.WebApp?.showAlert('✅ Куплено 5 приглашений за 300 VERA!');
      setShowBuyModal(false);

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to buy invites:', error);
      window.Telegram?.WebApp?.showAlert('Ошибка при покупке приглашений');
    } finally {
      setBuying(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="invites-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка приглашений...</p>
        </div>
      </div>
    );
  }

  const availableInvites = (inviteInfo?.invite_limit || 0) - (inviteInfo?.invites_used || 0);

  return (
    <div className="invites-page">
      <div className="invites-header">
        <div className="points-display">
          <span className="points-icon">⭐</span>
          <span className="points-value">{veraBalance?.vera_points || 0}</span>
          <span className="points-label">VERA</span>
        </div>
        <div className="user-greeting">
          {window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || 'Гость'} 👋
        </div>
      </div>

      {/* Invite Stats Card */}
      <div className="invite-stats-card">
        <div className="invite-stats-grid">
          <div className="invite-stat-item">
            <div className="invite-stat-value">{inviteInfo?.invites_used || 0}</div>
            <div className="invite-stat-label">Использовано</div>
          </div>
          <div className="invite-stat-item">
            <div className="invite-stat-value highlight">{availableInvites}</div>
            <div className="invite-stat-label">Доступно</div>
          </div>
          <div className="invite-stat-item">
            <div className="invite-stat-value">{inviteInfo?.invite_limit || 0}</div>
            <div className="invite-stat-label">Всего</div>
          </div>
        </div>
      </div>

      {/* Invite Link Card */}
      <div className="invite-link-card">
        <div className="invite-link-header">
          <span className="invite-link-icon">🔗</span>
          <span className="invite-link-title">Ваша ссылка-приглашение</span>
        </div>
        <div className="invite-link-url">
          {inviteInfo?.invite_link}
        </div>
        <div className="invite-link-actions">
          <button className="btn-invite-action" onClick={copyInviteLink}>
            📋 Копировать
          </button>
          <button className="btn-invite-action primary" onClick={shareInviteLink}>
            📤 Поделиться
          </button>
        </div>
      </div>

      {/* Buy More Invites Card */}
      <div className="buy-invites-card" onClick={() => setShowBuyModal(true)}>
        <div className="buy-invites-icon">🛒</div>
        <div className="buy-invites-content">
          <div className="buy-invites-title">Купить приглашения</div>
          <div className="buy-invites-subtitle">5 приглашений за 300 VERA</div>
        </div>
        <div className="buy-invites-arrow">→</div>
      </div>

      {/* Invited Users List */}
      <div className="invitees-section">
        <h3 className="section-title">
          Приглашенные пользователи ({invitees.length})
        </h3>

        {invitees.length === 0 ? (
          <div className="no-invitees-card">
            <span className="no-invitees-icon">👤</span>
            <p>Вы еще никого не пригласили</p>
            <p className="no-invitees-hint">Поделитесь ссылкой с друзьями!</p>
          </div>
        ) : (
          <>
            <div className="invitees-list">
              {invitees.slice(0, displayCount).map((invitee) => (
                <div key={invitee.id} className="invitee-card">
                  <div className="invitee-avatar">
                    {invitee.first_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="invitee-info">
                    <div className="invitee-name">
                      {invitee.first_name} {invitee.last_name || ''}
                    </div>
                    {invitee.username && (
                      <div className="invitee-username">@{invitee.username}</div>
                    )}
                    <div className="invitee-date">
                      Присоединился: {formatDate(invitee.invited_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {invitees.length > displayCount && (
              <button
                className="btn-show-more"
                onClick={() => setDisplayCount(displayCount + 3)}
              >
                Показать еще ({invitees.length - displayCount})
              </button>
            )}
          </>
        )}
      </div>

      {/* Buy Invites Modal */}
      {showBuyModal && (
        <div className="modal-overlay" onClick={() => !buying && setShowBuyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Купить приглашения</h3>
            <p className="modal-description">
              Вы можете купить 5 дополнительных приглашений за 300 VERA очков
            </p>
            <div className="modal-stats">
              <div className="modal-stat">
                <span className="modal-stat-label">У вас сейчас:</span>
                <span className="modal-stat-value">{veraBalance?.vera_points || 0} VERA</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">Стоимость:</span>
                <span className="modal-stat-value">300 VERA</span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">После покупки:</span>
                <span className="modal-stat-value">{(veraBalance?.vera_points || 0) - 300} VERA</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setShowBuyModal(false)}
                disabled={buying}
              >
                Отмена
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleBuyInvites}
                disabled={buying || (veraBalance?.vera_points || 0) < 300}
              >
                {buying ? 'Покупка...' : 'Купить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invites;
